import { Component, h, Listen, Prop, State, Watch } from '@stencil/core';
import { DefaultLanguages } from '../../../../models/DefaultLanguages';
import { Language } from '../../../../models/Language';
import getEnv from '../../../../utils/env';
import { HeadLinkService } from '../../service/head-link.service';

/**
 * @internal
 */
@Component({
  tag: 'bahf-language-switch-desktop',
  shadow: false,
})
export class LanguageSwitchDesktop {
  private env = getEnv();
  @Prop() hfConfigLanguages: Language[] | null = [];

  @Watch('currentLanguage')
  handleCurrentLanguageChange() {
    this.dropdownIsOpen = false;
  }

  @Prop() currentLanguage: Language = DefaultLanguages.de;

  @Prop() navMetaDropdownAriaLabel: string;
  @Prop() srSelectionLabel: string;

  @State() dropdownIsOpen = false;

  dropdownElem: HTMLDivElement;
  dropdownItemToFocus = 0;

  @State() alternateLinkLanguages: Language[] = HeadLinkService.readLinksFromHead();
  private headLinkService = new HeadLinkService();

  connectedCallback() {
    this.headLinkService.observeLinksAndHead(() => {
      this.alternateLinkLanguages = HeadLinkService.readLinksFromHead();
    });
  }

  disconnectedCallback() {
    this.headLinkService.disconnectObservers();
  }

  render() {
    const languageSwitchList: Language[] = this.hfConfigLanguages?.length > 0
      ? [ ...this.hfConfigLanguages ]
      : [ ...this.alternateLinkLanguages ];

    return (
      <ul class="container-fluid">
        {SignLanguage(this.currentLanguage, this.env.gebaerdenSprachEndpoint)}
        {SimpleLanguage(this.currentLanguage, this.env.leichteSprachEndpoint)}

        {languageSwitchList?.length > 0 && <li class="dropdown">
          <a
            href="#"
            class="ba-link-language"
            id="activeLanguageDropdown"
            role="button"
            aria-label={this.navMetaDropdownAriaLabel}
            aria-haspopup="true"
            aria-expanded={this.dropdownIsOpen.toString()}
            data-bs-toggle="dropdown"
            data-bs-offset="0,4"
            onClick={this.toggleDropdown.bind(this)}
            onKeyDown={this.toggleDropdownOrFocusFirst.bind(this)}
            data-testid="bahf-language-switch-desktop-button"
          >
            {this.currentLanguage.label}
          </a>

          <div
            class={`dropdown-menu dropdown-menu-right ${this.dropdownIsOpen ? 'show' : ''}`}
            aria-labelledby="activeLanguageDropdown"
            ref={(el) => (this.dropdownElem = el)}
            role="menu"
            onKeyDown={this.changeFocusedLanguageInDropdown.bind(this)}
            data-testid="bahf-language-switch-desktop-list"
          >

            {[ ...languageSwitchList ]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((language) => (
              <bahf-header-lang-switch-list-item
                key={language.code}
                language={language}
                active={language.code == this.currentLanguage.code}
                srSelectionLabel={this.srSelectionLabel}
                data-testid={'bahf-language-switch-desktop-' + language.code.toLowerCase()}
              />
            ))}

          </div>
        </li>}
      </ul>
    );
  }

  toggleDropdownOrFocusFirst(event) {
    if (event.code === 'Space') {
      this.toggleDropdown(event);
    }
    if (event.code === 'ArrowDown') {
      this.dropdownElem.querySelectorAll('a')[0].focus();
      event.preventDefault();
    }
  }

  toggleDropdown(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.dropdownIsOpen) {
      this.openDropdown();
      return;
    }
    this.closeDropdown();

    // verhindert die Verarbeitung des Events durch Bootstrap
    event.stopImmediatePropagation();
  }

  openDropdown() {
    this.dropdownIsOpen = true;
  }

  closeDropdown() {
    this.dropdownIsOpen = false;
  }

  changeFocusedLanguageInDropdown(event) {
    // navigate through Dropdown with arrow Keys
    const numberOfDropdowns = this.dropdownElem.querySelectorAll('a').length - 1;
    switch (event.code) {
      case 'ArrowDown':
        this.focusNext(this.dropdownItemToFocus, numberOfDropdowns, event);
        break;
      case 'ArrowUp':
        this.focusPrevious(this.dropdownItemToFocus, event);
        break;
      case 'Tab':
        this.changeFocused(this.dropdownItemToFocus, numberOfDropdowns, event);
        break;
    }
  }

  changeFocused(focusedElement, numberOfDropdowns: number, e: KeyboardEvent) {
    if (!e.shiftKey) {
      if (focusedElement === numberOfDropdowns) {
        this.closeDropdown();
        this.dropdownItemToFocus = 0;
      }
      if (focusedElement < numberOfDropdowns) {
        this.dropdownItemToFocus++;
      }
    } else {
      if (this.dropdownItemToFocus > 0) {
        this.dropdownItemToFocus--;
      }
    }
  }

  focusPrevious(focusedElement, e: KeyboardEvent) {
    if (focusedElement > 0) {
      this.dropdownItemToFocus--;
    }
    this.dropdownElem.querySelectorAll('a')[this.dropdownItemToFocus].focus();
    e.preventDefault();
  }

  focusNext(focusedElement, numberOfDropdowns: number, e: KeyboardEvent) {
    if (focusedElement < numberOfDropdowns) {
      this.dropdownItemToFocus++;
    }
    this.dropdownElem.querySelectorAll('a')[this.dropdownItemToFocus].focus();
    e.preventDefault();
  }

  /** registriert einen globalen Eventlistener auf dem "document" */
  @Listen('click', { target: 'document' })
  documentOnClick(event) {
    const path = event.composedPath();

    // dropdown schließen, falls nicht in das Dropdown-Element geklickt wird
    if (!path.includes(this.dropdownElem)) this.closeDropdown();
  }

  @Listen('keydown', { target: 'document' })
  documentOnKeyDown(event) {
    // dropdown schließen, falls Escape gedrückt wird
    if (event.key === 'Escape' || event.code == 'Escape') this.closeDropdown();
  }
}

function SignLanguage(lang: Language, endpoint: string) {
  const de = DefaultLanguages.de.code;

  return (
    <li style={lang.code !== de ? { display: 'none' } : {}} aria-hidden={lang.code !== de}>
      <a href={endpoint}
         class="ba-link-sign-language"
         data-testid="bahf-button-sign-language-desktop">
        Gebärdensprache
      </a>
    </li>
  );
}

function SimpleLanguage(lang: Language, endpoint: string) {
  const de = DefaultLanguages.de.code;

  return (
    <li style={lang.code != de ? { display: 'none' } : {}} aria-hidden={lang.code != de}>
      <a href={endpoint}
         class="ba-link-simple-language"
         data-testid="bahf-button-simple-language-desktop">
        Leichte Sprache
      </a>
    </li>
  );
}
